import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Mail, X } from "lucide-react";
import { useLang, WA_LINK } from "../i18n";
import { scrollToId } from "../hooks/useLenis";

const NAV = [
  { id: "#home", key: "home" },
  { id: "#minerals", key: "minerals" },
  { id: "#services", key: "services" },
  { id: "#prices", key: "prices" },
  { id: "#about", key: "about" },
  { id: "#contact", key: "contact" },
];

export function Footer() {
  const { t } = useLang();
  const [modal, setModal] = useState(null);
  const legal = [
    { key: "privacy", title: t.footer.privacy, body: t.footer.privacyBody },
    { key: "terms", title: t.footer.terms, body: t.footer.termsBody },
    { key: "disclaimer", title: t.footer.disclaimer, body: t.footer.disclaimerBody },
  ];
  const active = legal.find((l) => l.key === modal);

  return (
    <footer data-testid="site-footer" className="relative border-t border-[#C9A227]/15 bg-[#050B14]">
      <div className="mx-auto max-w-7xl px-5 md:px-8 py-16 grid gap-12 md:grid-cols-4">
        <div className="md:col-span-2">
          <button data-testid="footer-logo" onClick={() => scrollToId("#home")} className="flex items-center gap-2.5" aria-label="MX Minerals home">
            <img src="/assets/logo-mark.png" alt="MX" className="h-10 w-auto" />
            <span className="font-body text-xs tracking-[0.35em] text-[#EDF1F6] mt-1">MINERALS</span>
          </button>
          <p className="mt-4 max-w-sm text-sm text-[#EDF1F6]">{t.footer.tagline}</p>
          <p className="mt-2 text-xs text-[#EDF1F6]/60">MineralsMX.com</p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#C9A227] mb-4">{t.footer.navigate}</p>
          <ul className="space-y-2.5">
            {NAV.map((l) => (
              <li key={l.key}>
                <button data-testid={`footer-link-${l.key}`} onClick={() => scrollToId(l.id)} className="text-sm text-[#EDF1F6] hover:text-[#C9A227] transition-colors duration-300">
                  {t.nav[l.key]}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#C9A227] mb-4">{t.footer.legal}</p>
          <ul className="space-y-2.5">
            {legal.map((l) => (
              <li key={l.key}>
                <button data-testid={`footer-legal-${l.key}`} onClick={() => setModal(l.key)} className="text-sm text-[#EDF1F6] hover:text-[#C9A227] transition-colors duration-300">
                  {l.title}
                </button>
              </li>
            ))}
          </ul>
          <p className="text-xs uppercase tracking-[0.3em] text-[#C9A227] mt-8 mb-3">{t.footer.contact}</p>
          <a data-testid="footer-whatsapp-link" href={WA_LINK} target="_blank" rel="noopener noreferrer" className="text-sm text-[#EDF1F6] hover:text-[#C9A227] transition-colors duration-300">
            +1 951-218-3981
          </a>
          <a data-testid="footer-email-link" href="mailto:INFO@MINERALSMX.COM" className="mt-2 flex items-center gap-2 text-sm text-[#EDF1F6] hover:text-[#C9A227] transition-colors duration-300">
            <Mail size={14} className="shrink-0 text-[#C9A227]" /> INFO@MINERALSMX.COM
          </a>
          <p className="mt-2 text-xs text-[#EDF1F6]/60 leading-relaxed">220 N Hargrave St., Apartment #1<br />Banning, CA 92220, USA</p>
        </div>
      </div>

      <div className="border-t border-white/5">
        <p className="mx-auto max-w-7xl px-5 md:px-8 pt-6 text-xs text-[#EDF1F6]/60" data-testid="footer-rights">{t.footer.rights}</p>
        <p className="mx-auto max-w-7xl px-5 md:px-8 py-6 text-[10px] leading-relaxed text-[#EDF1F6]/45" data-testid="footer-intel-disclaimer">{t.intel.legal}</p>
      </div>

      <AnimatePresence>
        {active && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-5" role="dialog" aria-modal="true" aria-label={active.title}>
            <motion.div
              className="absolute inset-0 bg-[#07111F]/85 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setModal(null)}
            />
            <motion.div
              data-testid="legal-modal"
              className="relative glass max-w-lg w-full p-8"
              style={{ borderRadius: "4px 24px 4px 24px" }}
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              <h3 className="font-display text-2xl text-[#F5F7FA]">{active.title}</h3>
              <div className="mt-2 h-px w-16 bg-gradient-to-r from-[#C9A227] to-transparent" />
              <p className="mt-5 text-sm text-[#EDF1F6] leading-relaxed">{active.body}</p>
              <button
                data-testid="legal-modal-close"
                onClick={() => setModal(null)}
                className="mt-7 inline-flex items-center gap-2 rounded-full border border-[#EDF1F6]/30 px-6 py-2.5 text-xs font-semibold tracking-widest text-[#F5F7FA] hover:border-[#C9A227] hover:text-[#C9A227] transition-[border-color,color] duration-300"
              >
                <X size={14} /> {t.footer.close}
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </footer>
  );
}
